import API_BASE_URL from 'src/constants';
import { getToken } from './authToken';

/**
 * A freshly issued JWT is occasionally rejected with 401 for a few hundred
 * milliseconds. `useUserProfile` has carried a private retry for exactly that
 * since forever (`profile.ts`), while `Auth.checkAuth` did not — so `Auth`
 * deleted a token that was perfectly valid a moment later and bounced the user
 * back to /login. Both call sites share this helper now.
 */
export const PROFILE_RETRY_DELAY_MS = 300;

const delay = (ms: number) =>
  new Promise<void>(resolve => {
    setTimeout(resolve, ms);
  });

const requestProfile = (token: string, signal?: AbortSignal) =>
  fetch(`${API_BASE_URL}portal/users/profile`, {
    headers: { Authorization: `Bearer ${token}` },
    signal,
  });

/**
 * Fetches `/users/profile` with the token currently in storage, retrying a
 * single 401 after a short delay.
 *
 * @returns the (possibly failed) `Response`, or `null` when there is no token
 *          at all — the caller decides what "not signed in" means.
 */
export const fetchAuthenticatedProfile = async (signal?: AbortSignal): Promise<Response | null> => {
  const token = getToken();
  if (!token) return null;

  const response = await requestProfile(token, signal);
  if (response.status !== 401) return response;

  await delay(PROFILE_RETRY_DELAY_MS);
  if (signal?.aborted) return response;

  // Re-read: the token may have been replaced (or removed) while we waited.
  const retryToken = getToken();
  if (!retryToken) return response;

  return requestProfile(retryToken, signal);
};
